<template>
  <div ref="wrapperRef" class="m-file-input" :class="{ 'm-file-input--preview-only': previewOnly }">
    <div
      v-if="previewOnly && tapeItems.length > 1"
      class="m-file-input-preview-only-nav"
    >
      <MButton
        icon="pi pi-chevron-left"
        rounded
        variant="outlined"
        :disabled="previewOnlyIndex <= 0"
        @click="shiftPreviewOnly(-1)"
      />
      <span class="preview-only-label">{{ getFileName(tapeItems[previewOnlyIndex]) }}</span>
      <MButton
        icon="pi pi-chevron-right"
        rounded
        variant="outlined"
        :disabled="previewOnlyIndex >= tapeItems.length - 1"
        @click="shiftPreviewOnly(1)"
      />
    </div>

    <div v-if="!previewOnly" class="m-file-input-picker" :class="fileInputClasses">
      <input
        ref="nativeFileInputRef"
        type="file"
        class="m-file-input-native-input"
        :multiple="multiple"
        :disabled="isInteractionDisabled"
        @change="onNativeFileChange"
      />
      <div class="m-file-input-header">
        <MButton
          icon="pi pi-tablet"
          color="secondary"
          outlined
          small
          disabled
          :title="t('attachments.scannerDev')"
        />
        <MButton
          v-if="showR7EditorButton"
          icon="pi pi-file-edit"
          color="secondary"
          outlined
          small
          @click="openR7EditorForPreview"
          :disabled="!canOpenR7ForPreview"
          :title="r7EditorButtonTitle"
        />
        <MButton
          icon="pi pi-upload"
          color="secondary"
          outlined
          small
          @click="openFileDialog"
          :disabled="isInteractionDisabled"
        />
        <MButton
          color="secondary"
          outlined
          small
          @click="downloadSelectedFiles"
          icon="pi pi-download"
          :disabled="!canDownloadSelectedFiles"
        />
        <MButton
          color="secondary"
          outlined
          small
          @click="removeFocusedFile"
          icon="pi pi-trash"
          :disabled="!canDeleteFocusedFile"
          :title="t('attachments.deleteSelected')"
        />
        <MButton
          color="secondary"
          outlined
          small
          @click="selectAll"
          icon="pi pi-copy"
          :disabled="tapeItems.length === 0"
        />
      </div>
      <AttachmentTapeGrid
        :files="tapeItems"
        :tape-badge="tapeBadge"
        :get-file-name="getFileName"
        :get-file-size="getFileSize"
        :get-card-thumb-url="preview.getCardThumbUrl"
        :format-size="formatSize"
        :get-file-icon="getFileIcon"
        :get-file-icon-class="getFileIconClass"
        :file-extension="fileExtension"
        :is-file-selected="selection.isFilePreviewFocused"
        :is-file-multi-selected="isFileMultiSelected"
        :get-sync-card-classes="getSyncCardClasses"
        :show-add-card="!isInteractionDisabled"
        @card-click="onFileCardClick"
        @add-click="openFileDialog"
      />
    </div>

    <div v-if="uploadInProgress" class="file-input-hint">Загрузка файла на сервер…</div>
    <div v-if="hint" class="file-input-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />

    <AttachmentPreviewPane
      :show-large-preview="showLargePreview"
      :file-to-preview="fileToPreview"
      :preview-loading="preview.previewLoading.value"
      :preview-error="preview.previewError.value"
      :preview-kind="preview.previewKind.value"
      :preview-display-url="preview.previewDisplayUrl.value"
      :office-inline-preview-enabled="officeInlinePreviewEnabled"
      :r7-inline-preview-host-id="r7InlinePreviewHostId"
      :hide-r7-inline-preview-mount="hideR7InlinePreviewMount"
      :show-r7-inline-preview-overlay="showR7InlinePreviewOverlay"
      :r7-inline-preview-overlay-text="r7InlinePreviewOverlayText"
      :r7-inline-preview-error="r7InlinePreviewError"
      :preview-placeholder-text="previewPlaceholderText"
      :get-file-name="getFileName"
    />
  </div>
</template>

<script lang="ts">
import UniData from '@/common/classes/UniData'
import type { FileItem } from '@/common/types/fileItem'
import {
  buildAttachmentsElementUniData,
  ensureAttachmentClientRowKey,
} from '@/common/utils/fileAttachmentsUniData'

export type { FileData, FileItem } from '@/common/types/fileItem'

export function fileItemFromNative(file: File): FileItem {
  const item: FileItem = {
    file,
    changed: true,
    fields: {
      name: { type: 'String', value: file.name },
      size: { type: 'Long', value: file.size },
    },
  }
  ensureAttachmentClientRowKey(item)
  return item
}
</script>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { usePrimeVue } from 'primevue/config'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import {
  buildAttachmentPropertiesPlainText,
  parseAttachmentSignatures,
} from '@/common/utils/attachmentPropertiesTooltip'
import {
  attachmentItemsReferSameRow,
  attachmentUiRowKey,
  ensureAttachmentClientRowKey,
  findRefilledLocalRowForNativeFile,
  isAttachmentItemChanged,
  rowHasServerCode,
  withAttachmentItemChanged,
} from '@/common/utils/fileAttachmentsUniData'
import { useI18n } from '@/common/composables/useI18n'
import { useAttachmentTapeState } from '@/common/composables/useAttachmentTapeState'
import { useAttachmentSelection } from '@/common/composables/useAttachmentSelection'
import { useAttachmentPreview } from '@/common/composables/useAttachmentPreview'
import MButton from '@/components/ui/MButton.vue'
import AttachmentTapeGrid from '@/components/ui/AttachmentTapeGrid.vue'
import AttachmentPreviewPane from '@/components/ui/AttachmentPreviewPane.vue'
import AttachmentTapeSyncBadge from '@/components/ui/AttachmentTapeSyncBadge.vue'
import { useAttachmentTapeBadge } from '@/common/composables/useAttachmentTapeBadge'
import type { AttachmentTapeBadgeUi } from '@/common/utils/attachmentTapeRowStatus'
import {
  isOfficeFileName,
  isPdfFileName,
  isRasterFileName,
  resolveAttachmentPreviewKind,
} from '@/common/utils/attachmentPreviewUtils'
import {
  logAttachmentTapeDebug,
  type AttachmentTapeR7PolicyExplanation,
  type AttachmentTapeRowPolicyDebug,
} from '@/common/utils/attachmentDebugLog'
import { isAttachmentDownloadForbidden } from '@/common/utils/attachmentRpcPolicy'

function hasNativeFile(file: FileItem): file is FileItem & { file: File } {
  return Boolean(file.file && file.file instanceof File)
}

function fieldAsString(field: unknown): string | undefined {
  if (field == null) return undefined
  if (typeof field === 'string') return field
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const v = (field as { value: unknown }).value
    return v != null ? String(v) : undefined
  }
  return undefined
}

function getFileName(file: FileItem): string {
  if (hasNativeFile(file)) return file.file.name
  const fields = file.fields as Record<string, unknown>
  return (
    fieldAsString(fields.name) ??
    fieldAsString(fields.fileName) ??
    'Unnamed'
  )
}

function getFileType(file: FileItem): string {
  if (hasNativeFile(file)) return file.file.type.toLowerCase()

  const name = getFileName(file).toLowerCase()
  if (name.endsWith('.pdf')) return 'application/pdf'
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return 'application/vnd.ms-excel'
  if (name.endsWith('.doc') || name.endsWith('.docx')) return 'application/msword'
  if (
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.gif')
  )
    return 'image'
  return ''
}

function getFileSize(file: FileItem): number {
  if (hasNativeFile(file)) return file.file.size
  const sizeField = file.fields.size
  return sizeField ? Number(sizeField.value) || 0 : 0
}

interface Props {
  modelValue?: FileItem[]
  tableFieldName?: string
  label?: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  multiple?: boolean
  clearable?: boolean
  errorMessages?: string | string[]
  previewOnly?: boolean
  tapeWithFilesOnly?: boolean
  autoShowFirstElement?: boolean
  fetchAttachmentBlob?: (file: FileItem) => Promise<Blob | null>
  uploadInProgress?: boolean
  showR7EditorButton?: boolean
  canOpenR7Editor?: (file: FileItem) => boolean
  r7InlinePreviewHostId?: string
  r7InlinePreviewOverlay?: 'loading' | 'awaiting_ws' | 'editor_open' | null
  r7InlinePreviewError?: string | null
  canPreviewR7Inline?: (file: FileItem) => boolean
  resolveAttachmentTapeBadge?: (file: FileItem) => AttachmentTapeBadgeUi
  resolveAttachmentTapeR7Debug?: (file: FileItem) => AttachmentTapeR7PolicyExplanation
  isR7ModalOpenForFile?: (file: FileItem) => boolean
  onRemoveAttachmentSideEffects?: (file: FileItem) => void
}

const $primevue = usePrimeVue()
const { t } = useI18n()
const wrapperRef = ref<HTMLElement | null>(null)
const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  tableFieldName: 'attachments',
  label: '',
  placeholder: undefined,
  hint: '',
  disabled: false,
  multiple: true,
  clearable: true,
  errorMessages: () => [],
  previewOnly: false,
  tapeWithFilesOnly: false,
  autoShowFirstElement: false,
  uploadInProgress: false,
  showR7EditorButton: false,
  r7InlinePreviewHostId: '',
  r7InlinePreviewOverlay: null,
  r7InlinePreviewError: null,
})

const emit = defineEmits<{
  'update:modelValue': [files: FileItem[]]
  remove: [index: number]
  'download-file': [file: FileItem]
  'open-r7-editor': [file: FileItem]
  'tape-preview-focus': [file: FileItem | null]
}>()

const nativeFileInputRef = ref<HTMLInputElement | null>(null)
const modelValueRef = toRef(props, 'modelValue')

const showLargePreview = computed(() => !props.tapeWithFilesOnly)
const isInteractionDisabled = computed(() => props.disabled || props.uploadInProgress)

function normalizeIncomingFileItem(item: unknown): FileItem {
  if (item instanceof File) return fileItemFromNative(item)
  const row = item as FileItem
  if (isAttachmentItemChanged(row)) {
    return withAttachmentItemChanged(row)
  }
  return row
}

function logTapeDebug(
  phase: 'tape-sync' | 'focus',
  items: FileItem[],
  focused?: FileItem | null,
): void {
  const focusedIndex =
    focused != null
      ? items.findIndex((item) => attachmentUiRowKey(item) === attachmentUiRowKey(focused))
      : undefined
  logAttachmentTapeDebug(phase, {
    tableFieldName: props.tableFieldName,
    items,
    focused: focused ?? null,
    focusedIndex: focusedIndex != null && focusedIndex >= 0 ? focusedIndex : undefined,
    resolveRowPolicy: (item) => resolveTapeRowPolicyDebug(item),
  })
}

const tape = useAttachmentTapeState({
  modelValue: modelValueRef,
  normalizeIncoming: normalizeIncomingFileItem,
  emitUpdate: (items) => emit('update:modelValue', items),
  onTapeChanged: (items) => {
    logTapeDebug('tape-sync', items, fileToPreview.value)
  },
})

const { tapeItems, tombstoneAttachmentItem, commitTapeUpdate, removeTapeRow, appendTapeRows, resolveRowInTape } =
  tape

const selection = useAttachmentSelection({
  tapeItems,
  tapeWithFilesOnly: toRef(props, 'tapeWithFilesOnly'),
  showLargePreview,
})

const {
  selectedFiles,
  fileToPreview,
  previewOnlyIndex,
  rebindFileToPreview,
  openPreviewForFile,
  setPreviewFile,
  selectAll,
  clearSelection,
  shiftPreviewOnly,
  applyAutoShowFirstElement,
  getDeleteTargetFile,
} = selection

const tapeBadge = useAttachmentTapeBadge({
  files: tapeItems,
  resolveRowBadge: computed(() => props.resolveAttachmentTapeBadge),
})

const preview = useAttachmentPreview({
  fileToPreview,
  showLargePreview,
  fetchAttachmentBlob: (file) => props.fetchAttachmentBlob?.(file) ?? Promise.resolve(null),
  getFileName,
  hasNativeFile,
})

function isDeleteBlockedForFile(file: FileItem): boolean {
  if (props.isR7ModalOpenForFile?.(file)) return true
  return props.resolveAttachmentTapeBadge?.(file).clickBlocked ?? false
}

function canDeleteFile(file: FileItem | null): file is FileItem {
  if (!file || isInteractionDisabled.value) return false
  return !isDeleteBlockedForFile(file)
}

const canDeleteFocusedFile = computed(() =>
  canDeleteFile(getDeleteTargetFile(resolveRowInTape)),
)

const canDownloadSelectedFiles = computed(() =>
  selectedFiles.value.some((file) => !isAttachmentDownloadForbidden(file)),
)

const canOpenR7ForPreview = computed(() => {
  const file = fileToPreview.value
  if (!file || !props.showR7EditorButton) return false
  return props.canOpenR7Editor?.(file) ?? false
})

const r7EditorButtonTitle = computed(() => {
  if (!canOpenR7ForPreview.value) {
    return t('attachments.openInR7Hint')
  }
  return t('attachments.openInR7')
})

const effectivePlaceholder = computed(() => props.placeholder ?? t('attachments.chooseFiles'))

const fileInputClasses = computed(() => [
  'm-file-input',
  {
    'm-file-input--disabled': props.disabled,
    'm-file-input--error': hasErrors.value,
  },
])

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const hasErrors = computed(() => normalizedErrors.value.length > 0)

function filterNewNativePicks(picked: File[], tapeRows: FileItem[]): File[] {
  const onTape = new Set(
    tapeRows.map((item) => item.file).filter((file): file is File => file instanceof File),
  )
  return picked.filter((file) => !onTape.has(file))
}

function resetNativeFileInput(): void {
  const input = nativeFileInputRef.value
  if (input) input.value = ''
}

function onNativeFileChange(event: Event): void {
  const input = event.target as HTMLInputElement
  const picked = filterNewNativePicks(Array.from(input.files ?? []), tapeItems.value)
  resetNativeFileInput()
  if (picked.length === 0) return

  const asItems = picked.map((f) => fileItemFromNative(f))
  const next = props.multiple
    ? appendTapeRows(asItems)
    : asItems.length > 0
      ? [asItems[0]!]
      : []
  commitTapeUpdate(next, 'user')
  openPreviewForFile(next[next.length - 1])
}

const openFileDialog = () => {
  if (isInteractionDisabled.value) return
  nativeFileInputRef.value?.click()
}

function openR7EditorForPreview() {
  const file = fileToPreview.value
  if (!file || !canOpenR7ForPreview.value) return
  emit('open-r7-editor', file)
}

const officeInlinePreviewEnabled = computed(() => {
  const file = fileToPreview.value
  if (!file || preview.previewKind.value !== 'office') return false
  if (props.r7InlinePreviewOverlay === 'editor_open' || props.r7InlinePreviewOverlay === 'awaiting_ws') {
    return true
  }
  return props.canPreviewR7Inline?.(file) ?? false
})

const hideR7InlinePreviewMount = computed(
  () =>
    props.r7InlinePreviewOverlay === 'editor_open'
    || props.r7InlinePreviewOverlay === 'awaiting_ws',
)

const showR7InlinePreviewOverlay = computed(() => Boolean(props.r7InlinePreviewOverlay))

const r7InlinePreviewOverlayText = computed(() => {
  if (props.r7InlinePreviewOverlay === 'awaiting_ws') return t('attachments.awaitingSave')
  if (props.r7InlinePreviewOverlay === 'editor_open') return t('attachments.editorOpen')
  return t('attachments.previewLoading')
})

const previewPlaceholderText = computed(() => {
  if (!fileToPreview.value) return ''
  const name = getFileName(fileToPreview.value)
  if (isOfficeFileName(name)) {
    if (officeInlinePreviewEnabled.value) return ''
    return canOpenR7ForPreview.value
      ? t('attachments.previewClickOpen')
      : t('attachments.previewUnavailableExternal')
  }
  if (isPdfFileName(name) && !preview.previewDisplayUrl.value) {
    return t('attachments.previewPdfUnavailable')
  }
  if (isRasterFileName(name) && !preview.previewDisplayUrl.value) {
    return t('attachments.previewImageUnavailable')
  }
  return t('attachments.previewTypeUnavailable')
})

function resolveTapeRowPolicyDebug(file: FileItem): AttachmentTapeRowPolicyDebug {
  const name = getFileName(file)
  const base: AttachmentTapeRowPolicyDebug = {
    canOpenR7: props.showR7EditorButton ? (props.canOpenR7Editor?.(file) ?? false) : false,
    canPreviewInline: props.canPreviewR7Inline?.(file) ?? false,
    syncPhase: props.resolveAttachmentTapeBadge?.(file).phase ?? null,
    previewKind: resolveAttachmentPreviewKind(name),
    propsWired: {
      showR7EditorButton: props.showR7EditorButton,
      hasCanOpenR7Editor: typeof props.canOpenR7Editor === 'function',
      hasCanPreviewR7Inline: typeof props.canPreviewR7Inline === 'function',
      hasTapeR7DebugResolver: typeof props.resolveAttachmentTapeR7Debug === 'function',
    },
  }
  const r7 = props.resolveAttachmentTapeR7Debug?.(file)
  return r7 ? { ...base, r7 } : base
}

const removeFocusedFile = () => {
  const target = getDeleteTargetFile(resolveRowInTape)
  if (!canDeleteFile(target)) return

  props.onRemoveAttachmentSideEffects?.(target)
  tombstoneAttachmentItem(target)
  preview.clearThumbCache()
  commitTapeUpdate(removeTapeRow(target), 'user')
  clearSelection()
  fileToPreview.value = null
}

const downloadSelectedFiles = () => {
  selectedFiles.value
    .filter((file) => !isAttachmentDownloadForbidden(file))
    .forEach((file) => emit('download-file', file))
}

function onFileCardClick(event: MouseEvent | undefined, file: FileItem) {
  setPreviewFile(event, file)
  logTapeDebug('focus', tapeItems.value, file)
}

function isFileMultiSelected(file: FileItem): boolean {
  return selectedFiles.value.some((f) => attachmentItemsReferSameRow(f, file))
}

function getSyncCardClasses(file: FileItem) {
  return tapeBadge.getBadgeUi(file)
}

function getFocusedAttachmentItem(): FileItem | null {
  return fileToPreview.value ?? tapeItems.value[0] ?? null
}

function swingGetData(snapshotItems?: FileItem[]): UniData {
  const items = Array.isArray(snapshotItems) ? snapshotItems : tapeItems.value
  const ud = new UniData()
  ud.add(
    buildAttachmentsElementUniData(props.tableFieldName, items, {
      selectedItem: getFocusedAttachmentItem(),
    }),
  )
  return ud
}

/** После store write: rebind preview (controlled model, без reconcile). */
function syncAttachmentFileItems(_items: FileItem[]): void {
  preview.clearThumbCache()
  rebindFileToPreview(resolveRowInTape)
}

function appendFile(file: File): void {
  const refilled = findRefilledLocalRowForNativeFile(tapeItems.value, file)
  if (refilled) {
    const merged = withAttachmentItemChanged({
      ...refilled,
      file,
      changed: true,
    })
    ensureAttachmentClientRowKey(merged)
    const next = tapeItems.value.map((row) =>
      attachmentItemsReferSameRow(row, refilled) ? merged : row,
    )
    commitTapeUpdate(next, 'user')
    openPreviewForFile(merged)
    return
  }

  const item = fileItemFromNative(file)
  commitTapeUpdate(appendTapeRows([item]), 'user')
  openPreviewForFile(item)
}

function appendFileCopies(files: FileItem[]): void {
  if (!Array.isArray(files) || files.length === 0) return
  const marked = files.map((item) => {
    const fields = (item.fields ?? {}) as Record<string, unknown>
    const isNewCopy = item.file instanceof File && !rowHasServerCode(fields)
    if (item.changed === true || isNewCopy) {
      return withAttachmentItemChanged(item)
    }
    return item
  })
  commitTapeUpdate(appendTapeRows(marked), 'user')
}

function getAttachmentFileItems(): FileItem[] {
  return tapeItems.value
}

watch(fileToPreview, (file) => {
  emit('tape-preview-focus', file ?? null)
})

watch(
  () => props.modelValue,
  () => {
    rebindFileToPreview(resolveRowInTape)
    applyAutoShowFirstElement(props.autoShowFirstElement)
  },
  { deep: true },
)

watch(
  () => props.previewOnly,
  (only) => {
    if (only) applyAutoShowFirstElement(props.autoShowFirstElement)
  },
  { immediate: true },
)

watch(
  tapeItems,
  (items) => {
    applyAutoShowFirstElement(props.autoShowFirstElement)
    if (items.length === 0) {
      fileToPreview.value = null
    }
  },
  { immediate: true },
)

const formatSize = (bytes: number) => {
  const k = 1024
  const dm = 3
  const sizes = $primevue.config.locale?.fileSizeTypes || ['B']
  if (bytes === 0) {
    return `0 ${sizes[0]}`
  }

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm))

  return `${formattedSize} ${sizes[i]}`
}

const getFileIcon = (file: FileItem) => {
  const type = getFileType(file)
  const name = getFileName(file).toLowerCase()
  if (type.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx')) {
    return 'pi pi-file-excel'
  } else if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) {
    return 'pi pi-file-word'
  } else if (type.includes('pdf')) {
    return 'pi pi-file-pdf'
  }
  return 'pi pi-file'
}

const getFileIconClass = (file: FileItem) => {
  const type = getFileType(file)
  const name = getFileName(file).toLowerCase()
  if (type.includes('excel') || name.endsWith('.xls') || name.endsWith('.xlsx')) return 'excel'
  if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return 'word'
  if (type.includes('pdf')) return 'pdf'
  return 'default'
}

const fileExtension = (file: FileItem) => {
  const name = getFileName(file)
  const parts = name.split('.')
  return parts.length > 1 ? parts.pop()!.toUpperCase() : 'FILE'
}

defineExpose({
  appendFile,
  appendFileCopies,
  swingGetData,
  getAttachmentFileItems,
  getFocusedAttachmentItem,
  syncAttachmentFileItems,
})
</script>

<style scoped lang="scss">
.m-file-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.m-file-input-native-input {
  display: none;
}

.m-file-input-picker {
  --p-fileupload-background: #e5e7eb;
  --p-fileupload-border-color: #ffffff;
}

.file-input-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.m-file-input-header {
  display: flex;
  gap: 8px;
}

.m-file-input-preview-only-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .preview-only-label {
    flex: 1;
    text-align: center;
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.m-file-input--preview-only {
  .m-file-input-preview {
    margin-top: 0;
  }
}

.m-file-input--disabled {
  opacity: 0.6;
}
</style>
